<template>
  <div class="workflow-overview-tile" data-cy="workflow-overview-tile">
    <div class="tile-header">
      <div class="tile-header__label">
        <a :href="buildUrl(workflow)">{{ workflow.JobId }}</a>
      </div>
      <span
        class="tile-status"
        :class="workflow.StatusAsString?.toLowerCase()"
        >{{
          enumHelper.splitByCapitalLetter(EnumWorkflowStatus[workflow.Status!])
        }}</span
      >
      <div class="tile-body__actions">
        <img
          class="private-notes-icon"
          :src="
            workflow.LatestUserComment
              ? privateNotesIconActive
              : privateNotesIcon
          "
          @click="onNotesClick()"
          alt="Click to add private notes"
          data-cy="private-notes-action"
        />
        <img
          class="urgent-icon"
          :src="workflow.IsUrgent ? urgentIcon : notUrgentIcon"
          @click="onUrgentClick()"
          alt="Click to mark urgent"
          data-cy="urgent-action"
        />
      </div>
    </div>
    <div class="tile-body">
      <div class="tile-body__items-wrapper">
        <div class="tile-body__item">
          <span class="tile-body__item-label">Request Type</span>
          <span class="tile-body__item-value">{{
            enumHelper.splitByCapitalLetter(
              EnumSubmissionType[workflow.JobType!]
            )
          }}</span>
        </div>
        <div class="tile-body__item">
          <span class="tile-body__item-label">Company</span>
          <span class="tile-body__item-value"
            >{{ workflow.CompanyName }} / {{ workflow.CompanyId }}</span
          >
        </div>
      </div>

      <Transition>
        <div v-if="expand">
          <div class="tile-body__items-wrapper">
            <div class="tile-body__item">
              <span class="tile-body__item-label">RC</span>
              <span class="tile-body__item-value">{{
                workflow.CompanyRc
              }}</span>
            </div>
            <div class="tile-body__item">
              <span class="tile-body__item-label">Assigned to</span>
              <span class="tile-body__item-value">{{
                workflow.CurrentlyAssignedToRfr
              }}</span>
            </div>
            <div class="tile-body__item">
              <span class="tile-body__item-label">Date Modified</span>
              <span class="tile-body__item-value">{{
                moment(workflow.LastModified).format("MM/DD/YYYY h:mm a")
              }}</span>
            </div>
            <div class="tile-body__item">
              <span class="tile-body__item-label">Modified By</span>
              <span class="tile-body__item-value">{{
                workflow.LastModifiedByRfr
              }}</span>
            </div>
          </div>
          <div v-if="workflow.LatestComment" class="tile-body__item">
            <span class="tile-body__item-label">Latest Comment</span>
            <span class="tile-body__item-value">{{
              workflow.LatestComment
            }}</span>
          </div>
        </div>
      </Transition>
      <div @click="expand = !expand" class="tile-body__expand">
        <div class="tile-body__expand-icon">
          <font-awesome-icon :icon="expand ? 'chevron-up' : 'chevron-down'" />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { WfSubmissionProductAndIngredient } from "@/Domain/WfSubmissionProductAndIngredient";
import { ref } from "vue";
import moment from "moment";
import { EnumSubmissionType } from "@/Domain/EnumSubmissionType";
import enumHelper from "@/shared/helpers/enumHelper";
import { EnumWorkflowStatus } from "@/Domain/EnumWorkflowStatus";
import urgentIcon from "@/assets/img/Urgent1Red.png";
import notUrgentIcon from "@/assets/img/Urgent1Red_na.png";
import privateNotesIcon from "@/assets/img/pencil_na.png";
import privateNotesIconActive from "@/assets/img/pencil.png";

const props = defineProps<{
  workflow: WfSubmissionProductAndIngredient;
}>();

const emit = defineEmits<{
  (e: "privateNotesClicked", wf: WfSubmissionProductAndIngredient): void;
  (e: "urgentClicked", wf: WfSubmissionProductAndIngredient): void;
}>();

const expand = ref(false);

const buildUrl = function (workflow: WfSubmissionProductAndIngredient) {
  let url = "";
  if (
    workflow.id > 0 &&
    (workflow.JobType === EnumSubmissionType.ProductAdd ||
      workflow.JobType === EnumSubmissionType.ProductCopy ||
      workflow.JobType === EnumSubmissionType.ProductTerminate)
  ) {
    url = `WorkflowDetail?workflowId=${workflow.id}&HistoryMode=HideSome`;
  } else if (
    workflow.id > 0 &&
    (workflow.JobType === EnumSubmissionType.IngredientAdd ||
      workflow.JobType === EnumSubmissionType.IngredientDelete)
  ) {
    url = `WorkflowDetailIngredients?JobID=${workflow.JobId}&HistoryMode=HideSome`;
  }
  return url;
};

const onNotesClick = function () {
  emit("privateNotesClicked", props.workflow);
};

const onUrgentClick = function () {
  emit("urgentClicked", props.workflow);
};
</script>

<style scoped lang="scss">
.workflow-overview-tile {
  position: relative;
  background: #fff;
  border-radius: 4px;
  padding: 0;
  box-shadow: 0 0 10px #0000001a;

  .tile-body {
    &__items-wrapper {
      display: flex;
      flex-direction: row;
      flex-wrap: wrap;
      gap: 4px;
      justify-content: space-between;
    }

    &__item {
      display: flex;
      padding: 5px 15px;
      flex-direction: column;
      flex: 1 0 calc(50% - 8px);

      &-label {
        color: #999;
        font-size: 12px;
      }
      &-value {
        color: #212529;
        display: block;
        font-size: 14px;
      }
    }

    &__expand {
      display: flex;
      justify-content: center;
      padding: 5px 15px;
      cursor: pointer;
      font-size: 10px;
    }

    &__expand-icon {
      border: 1px solid #ccc;
      border-radius: 24px;
      padding: 2px 8px;
      height: 28px;
      display: flex;
      align-items: center;
      justify-content: center;

      &:hover {
        background: #f5f5f5;
      }
    }

    &__actions {
      display: flex;
      gap: 12px;
      flex: 1;
      justify-content: flex-end;
      align-items: center;

      .urgent-icon {
        cursor: pointer;
        width: 20px;
        height: 20px;
      }
      .private-notes-icon {
        cursor: pointer;
        width: 15px;
        height: 15px;
      }
    }
  }

  .tile-header {
    display: flex;
    padding: 16px 15px 5px 15px;
    align-items: center;
    font-size: 15px;
    font-weight: 600;
    color: #212529;
    gap: 12px;
  }

  .tile-status {
    background: #999;
    padding: 3px 8px;
    border-radius: 4px;
    color: white;
    text-wrap: nowrap;
    font-size: 12px;

    &.open,
    &.reopened {
      background: #78bb78;
    }

    &.inprogress,
    &.autoprocessed {
      background: #e7af4a;
    }
  }

  .v-enter-active,
  .v-leave-active {
    transition: all 0.5s ease;
    max-height: 900px;
  }
  .v-enter-from,
  .v-leave-to {
    transition: all 0.5s ease;
    opacity: 0;
    max-height: 0px;
  }
}
</style>
